import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit, Trash2, Package, Eye, EyeOff, MapPin, Calendar, Globe, Home, Search, Star, X, Building2, Upload, Image as ImageIcon, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PAX_ROWS = [10, 9, 8, 7, 6, 5, 4, 3, 2];

// Lowest per-adult (CPAI) price across all hotel options, or legacy fallback
const getPkgStartingPrice = (pkg) => {
  if (pkg.hotel_options && pkg.hotel_options.length > 0) {
    const prices = [];
    pkg.hotel_options.forEach(opt => {
      (opt.pax_rows || []).forEach(r => {
        const cpai = r.prices && r.prices.CPAI;
        if (cpai != null && cpai > 0) prices.push(Number(cpai));
      });
    });
    if (prices.length > 0) return Math.min(...prices);
  }
  return pkg.per_adult_price || pkg.price || 0;
};

const makeEmptyStay = () => ({ nights: "", destination: "", hotel_name: "", room_category: "" });

const makeEmptyHotelOption = () => ({
  id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  stays: [makeEmptyStay()],
  meal_plans: ["CPAI"],
  pax_rows: DEFAULT_PAX_ROWS.map(p => ({ pax: p, vehicle: "", prices: { CPAI: "" } })),
  cwb_price: "",
  cnb_price: "",
});

const emptyForm = {
  title: "", destination: "", trip_type: "domestic",
  duration_nights: 1, duration_days: 2, price: "",
  per_adult_price: "", per_cwb_price: "", per_cnb_price: "",
  hotel_options: [],
  description: "", highlights: "", inclusions: "", exclusions: "", terms_conditions: "",
  itinerary: [{ day: 1, title: "", description: "", activities: "", activity_items: [] }],
  itinerary_mode: "manual",
  image_url: "", category: "standard", is_featured: false, status: "active", publish_to: "agents"
};

const AdminPackages = () => {
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [filterType, setFilterType] = useState("all");
  const [audienceTab, setAudienceTab] = useState("agents"); // 'agents' | 'website'
  const [searchQuery, setSearchQuery] = useState("");
  const [destinations, setDestinations] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sightseeings, setSightseeings] = useState([]);
  const [activityModal, setActivityModal] = useState({ open: false, dayIdx: null });
  const [activitySearch, setActivitySearch] = useState("");

  useEffect(() => { fetchPackages(); fetchDestinations(); fetchSightseeings(); }, []);

  const fetchDestinations = async () => {
    try {
      const res = await axios.get(`${API}/destinations?status=active`);
      setDestinations(res.data || []);
    } catch {
      // Silent fail - destinations are optional for filtering
    }
  };

  const fetchSightseeings = async () => {
    try {
      const res = await axios.get(`${API}/sightseeing`, { headers: { Authorization: `Bearer ${token}` } });
      setSightseeings(res.data || []);
    } catch {
      // Silent fail
    }
  };

  // ===== Itinerary Auto-mode activity helpers =====
  const openActivityPicker = (dayIdx) => {
    setActivityModal({ open: true, dayIdx });
    setActivitySearch("");
  };

  const addActivityToDay = (tour) => {
    const dayIdx = activityModal.dayIdx;
    if (dayIdx === null) return;
    const day = formData.itinerary[dayIdx];
    const exists = (day.activity_items || []).some(a => a.id === tour.id);
    if (exists) { toast.error("Already added"); return; }
    const newItem = {
      id: tour.id,
      title: tour.tour_name || tour.name || "",
      description: tour.description || "",
      destination: tour.destination || "",
      price: tour.price_adult || tour.price || 0,
    };
    const updated = [...formData.itinerary];
    updated[dayIdx] = { ...day, activity_items: [...(day.activity_items || []), newItem] };
    setFormData({ ...formData, itinerary: updated });
    toast.success("Activity added");
  };

  const removeActivityFromDay = (dayIdx, activityIdx) => {
    const day = formData.itinerary[dayIdx];
    const updatedItems = (day.activity_items || []).filter((_, i) => i !== activityIdx);
    const updated = [...formData.itinerary];
    updated[dayIdx] = { ...day, activity_items: updatedItems };
    setFormData({ ...formData, itinerary: updated });
  };

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/packages`, { headers: { Authorization: `Bearer ${token}` } });
      setPackages(res.data);
    } catch { toast.error("Failed to load packages"); } finally { setLoading(false); }
  };

  const resetForm = () => { setFormData({ ...emptyForm, itinerary: [{ day: 1, title: "", description: "", activities: "", activity_items: [] }], hotel_options: [] }); setEditingPackage(null); };

  const openEditForm = (pkg) => {
    // Normalize hotel_options from backend (supports legacy flat fields migrated into stays[])
    const normalizedHotelOptions = (pkg.hotel_options || []).map(opt => {
      let stays = opt.stays;
      if (!stays || stays.length === 0) {
        // Legacy single-stay fallback
        stays = [{
          nights: opt.nights || "",
          destination: opt.destination || "",
          hotel_name: opt.hotel_name || "",
          room_category: opt.room_category || "",
        }];
      } else {
        stays = stays.map(s => ({
          nights: s.nights || "",
          destination: s.destination || "",
          hotel_name: s.hotel_name || "",
          room_category: s.room_category || "",
        }));
      }
      return {
        id: opt.id || `opt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        stays,
        meal_plans: opt.meal_plans && opt.meal_plans.length > 0 ? opt.meal_plans : ["CPAI"],
        pax_rows: (opt.pax_rows || DEFAULT_PAX_ROWS.map(p => ({ pax: p, vehicle: "", prices: { CPAI: "" } }))).map(r => ({
          pax: r.pax,
          vehicle: r.vehicle || "",
          prices: Object.keys(r.prices || {}).reduce((acc, k) => { acc[k] = r.prices[k] != null ? String(r.prices[k]) : ""; return acc; }, {}),
        })),
        cwb_price: opt.cwb_price != null ? String(opt.cwb_price) : "",
        cnb_price: opt.cnb_price != null ? String(opt.cnb_price) : "",
      };
    });

    setFormData({
      title: pkg.title, destination: pkg.destination, trip_type: pkg.trip_type || "domestic",
      duration_nights: pkg.duration_nights || parseInt(pkg.duration) || 1,
      duration_days: pkg.duration_days || (parseInt(pkg.duration) || 1) + 1,
      price: pkg.price?.toString() || "", per_adult_price: pkg.per_adult_price?.toString() || "",
      per_cwb_price: pkg.per_cwb_price?.toString() || "", per_cnb_price: pkg.per_cnb_price?.toString() || "",
      hotel_options: normalizedHotelOptions,
      description: pkg.description || "", highlights: (pkg.highlights || []).join("\n"),
      inclusions: (pkg.inclusions || []).join("\n"), exclusions: (pkg.exclusions || []).join("\n"),
      terms_conditions: pkg.terms_conditions || "",
      itinerary: pkg.itinerary?.length > 0 ? pkg.itinerary.map(d => ({
        day: d.day,
        title: d.title || "",
        description: d.description || "",
        activities: Array.isArray(d.activities) ? (typeof d.activities[0] === 'string' ? d.activities.join(", ") : "") : (d.activities || ""),
        activity_items: Array.isArray(d.activities) && d.activities.length > 0 && typeof d.activities[0] === 'object' ? d.activities : (d.activity_items || [])
      })) : [{ day: 1, title: "", description: "", activities: "", activity_items: [] }],
      itinerary_mode: pkg.itinerary_mode || "manual",
      image_url: pkg.image_url || "", category: pkg.category || "standard",
      is_featured: pkg.is_featured || false, status: pkg.status || "active",
      publish_to: pkg.publish_to || "agents"
    });
    setEditingPackage(pkg);
    setShowForm(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/packages/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url;
      if (url) {
        const fullUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        setFormData(prev => ({ ...prev, image_url: fullUrl }));
        toast.success("Image uploaded!");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData, price: parseFloat(formData.price) || 0,
      per_adult_price: parseFloat(formData.per_adult_price) || 0,
      per_cwb_price: parseFloat(formData.per_cwb_price) || 0,
      per_cnb_price: parseFloat(formData.per_cnb_price) || 0,
      hotel_options: (formData.hotel_options || []).map(opt => ({
        id: opt.id,
        stays: (opt.stays || []).map(s => ({
          nights: s.nights || "",
          destination: s.destination || "",
          hotel_name: s.hotel_name || "",
          room_category: s.room_category || "",
        })),
        meal_plans: opt.meal_plans || ["CPAI"],
        pax_rows: (opt.pax_rows || []).map(r => ({
          pax: parseInt(r.pax) || 0,
          vehicle: r.vehicle || "",
          prices: Object.keys(r.prices || {}).reduce((acc, k) => { acc[k] = parseFloat(r.prices[k]) || 0; return acc; }, {}),
        })),
        cwb_price: parseFloat(opt.cwb_price) || 0,
        cnb_price: parseFloat(opt.cnb_price) || 0,
      })),
      duration_nights: parseInt(formData.duration_nights) || 1,
      duration_days: parseInt(formData.duration_days) || 2,
      highlights: formData.highlights.split("\n").filter(h => h.trim()),
      inclusions: formData.inclusions.split("\n").filter(i => i.trim()),
      exclusions: formData.exclusions.split("\n").filter(x => x.trim()),
      terms_conditions: formData.terms_conditions || "",
      itinerary: formData.itinerary.map(d => ({
        day: d.day,
        title: d.title,
        description: d.description,
        activities: formData.itinerary_mode === "auto"
          ? (d.activity_items || [])
          : (typeof d.activities === 'string' ? d.activities.split(",").map(a => a.trim()).filter(Boolean) : d.activities)
      })),
      itinerary_mode: formData.itinerary_mode || "manual",
    };
    try {
      if (editingPackage) {
        await axios.put(`${API}/packages/${editingPackage.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Package updated!");
      } else {
        await axios.post(`${API}/packages`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Package created!");
      }
      setShowForm(false); resetForm(); fetchPackages();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save package");
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await axios.delete(`${API}/packages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Package deleted!"); fetchPackages();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleStatus = async (id) => {
    try {
      await axios.patch(`${API}/packages/${id}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Status updated!"); fetchPackages();
    } catch { toast.error("Failed to toggle status"); }
  };

  const updateItineraryDay = (idx, field, value) => {
    const updated = [...formData.itinerary];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormData({ ...formData, itinerary: updated });
  };

  const addItineraryDay = () => {
    const next = formData.itinerary.length + 1;
    setFormData({ ...formData, itinerary: [...formData.itinerary, { day: next, title: "", description: "", activities: "", activity_items: [] }] });
  };

  const removeItineraryDay = (idx) => {
    if (formData.itinerary.length <= 1) return;
    const updated = formData.itinerary.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 }));
    setFormData({ ...formData, itinerary: updated });
  };

  const handleNightsChange = (val) => {
    const n = parseInt(val) || 1;
    setFormData({ ...formData, duration_nights: n, duration_days: n + 1 });
  };

  // ===== Hotel Option helpers =====
  const addHotelOption = () => {
    setFormData({ ...formData, hotel_options: [...(formData.hotel_options || []), makeEmptyHotelOption()] });
  };

  const removeHotelOption = (idx) => {
    const updated = formData.hotel_options.filter((_, i) => i !== idx);
    setFormData({ ...formData, hotel_options: updated });
  };

  const updateHotelOption = (idx, field, value) => {
    const updated = [...formData.hotel_options];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormData({ ...formData, hotel_options: updated });
  };

  // ===== Stay helpers (segments within a Hotel Option) =====
  const addStay = (optIdx) => {
    const updated = [...formData.hotel_options];
    updated[optIdx] = { ...updated[optIdx], stays: [...(updated[optIdx].stays || []), makeEmptyStay()] };
    setFormData({ ...formData, hotel_options: updated });
  };

  const removeStay = (optIdx, stayIdx) => {
    const opt = formData.hotel_options[optIdx];
    if ((opt.stays || []).length <= 1) { toast.error("At least 1 stay is required"); return; }
    const updated = [...formData.hotel_options];
    updated[optIdx] = { ...opt, stays: opt.stays.filter((_, i) => i !== stayIdx) };
    setFormData({ ...formData, hotel_options: updated });
  };

  const updateStay = (optIdx, stayIdx, field, value) => {
    const opt = formData.hotel_options[optIdx];
    const updatedStays = [...opt.stays];
    updatedStays[stayIdx] = { ...updatedStays[stayIdx], [field]: value };
    const updated = [...formData.hotel_options];
    updated[optIdx] = { ...opt, stays: updatedStays };
    setFormData({ ...formData, hotel_options: updated });
  };

  const addMealPlan = (optIdx, planName) => {
    const name = (planName || "").trim().toUpperCase();
    if (!name) return;
    const opt = formData.hotel_options[optIdx];
    if (opt.meal_plans.includes(name)) { toast.error(`${name} already added`); return; }
    const updatedOpt = {
      ...opt,
      meal_plans: [...opt.meal_plans, name],
      pax_rows: opt.pax_rows.map(r => ({ ...r, prices: { ...r.prices, [name]: "" } })),
    };
    const updated = [...formData.hotel_options];
    updated[optIdx] = updatedOpt;
    setFormData({ ...formData, hotel_options: updated });
  };

  const removeMealPlan = (optIdx, planName) => {
    if (planName === "CPAI") { toast.error("CPAI is required and cannot be removed"); return; }
    const opt = formData.hotel_options[optIdx];
    const updatedOpt = {
      ...opt,
      meal_plans: opt.meal_plans.filter(p => p !== planName),
      pax_rows: opt.pax_rows.map(r => {
        const { [planName]: _removed, ...rest } = r.prices;
        return { ...r, prices: rest };
      }),
    };
    const updated = [...formData.hotel_options];
    updated[optIdx] = updatedOpt;
    setFormData({ ...formData, hotel_options: updated });
  };

  const addPaxRow = (optIdx) => {
    const opt = formData.hotel_options[optIdx];
    // find max pax excluding the fixed 2, add one above
    const nonTwoRows = opt.pax_rows.filter(r => r.pax !== 2);
    const maxPax = nonTwoRows.length > 0 ? Math.max(...nonTwoRows.map(r => r.pax)) : 2;
    const newPax = maxPax + 1;
    const newRow = { pax: newPax, vehicle: "", prices: opt.meal_plans.reduce((acc, p) => { acc[p] = ""; return acc; }, {}) };
    // insert new row at top (before others sorted desc), keeping 2 at bottom
    const updatedRows = [newRow, ...nonTwoRows.sort((a, b) => b.pax - a.pax), ...opt.pax_rows.filter(r => r.pax === 2)];
    const updated = [...formData.hotel_options];
    updated[optIdx] = { ...opt, pax_rows: updatedRows };
    setFormData({ ...formData, hotel_options: updated });
  };

  const removePaxRow = (optIdx, rowIdx) => {
    const opt = formData.hotel_options[optIdx];
    const row = opt.pax_rows[rowIdx];
    if (row.pax === 2) { toast.error("2 PAX row is required and cannot be removed"); return; }
    const updatedRows = opt.pax_rows.filter((_, i) => i !== rowIdx);
    const updated = [...formData.hotel_options];
    updated[optIdx] = { ...opt, pax_rows: updatedRows };
    setFormData({ ...formData, hotel_options: updated });
  };

  const updatePaxRow = (optIdx, rowIdx, field, value) => {
    const opt = formData.hotel_options[optIdx];
    const updatedRows = [...opt.pax_rows];
    if (field === "pax") {
      updatedRows[rowIdx] = { ...updatedRows[rowIdx], pax: parseInt(value) || 0 };
    } else if (field === "vehicle") {
      updatedRows[rowIdx] = { ...updatedRows[rowIdx], vehicle: value };
    } else {
      // field is meal plan key
      updatedRows[rowIdx] = { ...updatedRows[rowIdx], prices: { ...updatedRows[rowIdx].prices, [field]: value } };
    }
    const updated = [...formData.hotel_options];
    updated[optIdx] = { ...opt, pax_rows: updatedRows };
    setFormData({ ...formData, hotel_options: updated });
  };

  const filtered = packages.filter(p => {
    // Audience tab filter (legacy packages without publish_to default to 'agents')
    const pubTo = p.publish_to || "agents";
    if (audienceTab === "agents" && !(pubTo === "agents" || pubTo === "both")) return false;
    if (audienceTab === "website" && !(pubTo === "website" || pubTo === "both")) return false;
    if (filterType !== "all" && p.trip_type !== filterType) return false;
    if (searchQuery && !p.title.toLowerCase().includes(searchQuery.toLowerCase()) && !p.destination.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Package className="w-5 h-5 text-emerald-600" /></div>
              Readymade Packages
            </h1>
            <p className="text-sm text-gray-500 mt-1 ml-[52px]">Create and manage travel packages visible to all agents</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="create-package-btn">
            <Plus className="w-4 h-4" /> Create Package
          </Button>
        </div>

        {/* Audience Tabs (publish_to) */}
        <div className="flex items-center gap-2 border-b border-gray-200">
          <button
            onClick={() => setAudienceTab("website")}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${audienceTab === 'website' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="audience-tab-website"
          >
            <span className="inline-flex items-center gap-2"><Globe className="w-4 h-4" /> WEB <span className="text-[11px] font-normal text-gray-400">(Website only)</span></span>
          </button>
          <button
            onClick={() => setAudienceTab("agents")}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${audienceTab === 'agents' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="audience-tab-agents"
          >
            <span className="inline-flex items-center gap-2"><Users className="w-4 h-4" /> AGENTS <span className="text-[11px] font-normal text-gray-400">(Agents only)</span></span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search packages..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white border-gray-200" data-testid="search-packages" />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {["all", "domestic", "international"].map(t => (
              <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterType === t ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid={`filter-${t}`}>
                {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <Badge className="bg-gray-100 text-gray-600">{filtered.length} packages</Badge>
        </div>

        {/* Package Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-white rounded-xl animate-pulse border border-gray-100" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-white border-gray-200"><CardContent className="py-16 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg font-medium">No packages found</p>
            <p className="text-gray-400 text-sm mt-1">Create your first package to get started</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="packages-grid">
            {filtered.map(pkg => (
              <Card key={pkg.id} className={`bg-white border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow ${pkg.status === 'inactive' ? 'opacity-60' : ''}`} data-testid={`package-card-${pkg.id}`}>
                {/* Image */}
                <div className="h-40 bg-gradient-to-br from-emerald-100 to-teal-50 relative">
                  {pkg.image_url ? (
                    <img src={pkg.image_url} alt={pkg.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><MapPin className="w-12 h-12 text-emerald-200" /></div>
                  )}
                  <div className="absolute top-2 left-2 flex gap-1">
                    <Badge className={pkg.trip_type === 'international' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'}>
                      {pkg.trip_type === 'international' ? <Globe className="w-3 h-3 mr-1" /> : <Home className="w-3 h-3 mr-1" />}
                      {(pkg.trip_type || 'domestic').charAt(0).toUpperCase() + (pkg.trip_type || 'domestic').slice(1)}
                    </Badge>
                    {pkg.is_featured && <Badge className="bg-amber-500 text-white"><Star className="w-3 h-3 mr-1" />Featured</Badge>}
                    {(() => {
                      const pubTo = pkg.publish_to || 'agents';
                      const label = pubTo === 'website' ? 'WEB' : pubTo === 'both' ? 'WEB + AGENTS' : 'AGENTS';
                      const cls = pubTo === 'website' ? 'bg-purple-600 text-white' : pubTo === 'both' ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-white';
                      return <Badge className={cls}>{label}</Badge>;
                    })()}
                  </div>
                  <Badge className={`absolute top-2 right-2 ${pkg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {pkg.status || 'active'}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-gray-900 text-base mb-1" data-testid={`pkg-title-${pkg.id}`}>{pkg.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{pkg.destination}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{pkg.duration_nights || parseInt(pkg.duration) || 0}N / {pkg.duration_days || (parseInt(pkg.duration) || 0) + 1}D</span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">{pkg.description}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-bold text-emerald-700">
                        {getPkgStartingPrice(pkg) > 0 ? `INR ${getPkgStartingPrice(pkg).toLocaleString()}` : 'On Request'}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">/ person</span>
                      {pkg.hotel_options && pkg.hotel_options.length > 0 && (
                        <Badge className="ml-2 bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">{pkg.hotel_options.length} hotel {pkg.hotel_options.length === 1 ? 'option' : 'options'}</Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => toggleStatus(pkg.id)} className="text-gray-400 hover:text-amber-600" data-testid={`toggle-${pkg.id}`}>
                        {pkg.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditForm(pkg)} className="text-gray-400 hover:text-blue-600" data-testid={`edit-${pkg.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deletePackage(pkg.id)} className="text-gray-400 hover:text-red-600" data-testid={`delete-${pkg.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="max-w-5xl bg-white border-gray-200 text-gray-800 max-h-[92vh] overflow-y-auto" data-testid="package-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit' : 'Create'} Package</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <Tabs defaultValue="basic">
              <TabsList className="bg-gray-100">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="itinerary">Itinerary</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                {/* Publish To — WEB or AGENT */}
                <div>
                  <Label>Publish To *</Label>
                  <div className="mt-1.5 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, publish_to: 'website' })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${formData.publish_to === 'website' ? 'bg-purple-600 border-purple-700 text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50'}`}
                      data-testid="pkg-publish-web"
                    >
                      <Globe className="w-4 h-4" /> WEB <span className="text-[11px] font-normal opacity-80">(Website)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, publish_to: 'agents' })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${formData.publish_to === 'agents' ? 'bg-slate-700 border-slate-800 text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-slate-400 hover:bg-slate-50'}`}
                      data-testid="pkg-publish-agent"
                    >
                      <Users className="w-4 h-4" /> AGENT <span className="text-[11px] font-normal opacity-80">(Agents)</span>
                    </button>
                  </div>
                </div>

                {/* Trip Type */}
                <div>
                  <Label>Trip Type *</Label>
                  <Select value={formData.trip_type} onValueChange={v => setFormData({ ...formData, trip_type: v, destination: "" })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="pkg-trip-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domestic">Domestic</SelectItem>
                      <SelectItem value="international">International</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Destination (filtered by trip type) */}
                <div>
                  <Label>Destination *</Label>
                  <Select value={formData.destination} onValueChange={v => setFormData({ ...formData, destination: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="pkg-dest-select">
                      <SelectValue placeholder={`Select ${formData.trip_type === 'international' ? 'International' : 'Domestic'} destination`} />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations
                        .filter(d => formData.trip_type === 'international' ? d.country === 'International' : d.country !== 'International')
                        .map(d => (
                          <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                        ))}
                      {destinations.filter(d => formData.trip_type === 'international' ? d.country === 'International' : d.country !== 'International').length === 0 && (
                        <div className="p-3 text-sm text-gray-400 text-center">No {formData.trip_type} destinations. Add from Admin &gt; Resources.</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Package Title */}
                <div>
                  <Label>Package Title *</Label>
                  <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Kerala Bliss 5N/6D" required className="bg-gray-50 border-gray-200" data-testid="pkg-title-input" />
                </div>

                {/* Nights / Days */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nights</Label>
                    <Input type="number" min="1" value={formData.duration_nights} onChange={e => handleNightsChange(e.target.value)} className="bg-gray-50 border-gray-200" data-testid="pkg-nights" />
                  </div>
                  <div>
                    <Label>Days</Label>
                    <Input type="number" value={formData.duration_days} readOnly className="bg-gray-100 border-gray-200" />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="Brief package description..." className="bg-gray-50 border-gray-200" data-testid="pkg-desc" />
                </div>

                {/* Image Upload */}
                <div>
                  <Label>Package Image <span className="text-[10px] text-gray-400">(max 5MB, JPG/PNG/WebP • recommended 1200×600 px — fits the package card)</span></Label>
                  <div className="mt-1 flex items-center gap-4">
                    {formData.image_url ? (
                      <div className="relative w-32 h-20 rounded-md overflow-hidden border border-gray-200 bg-gray-50">
                        <img src={formData.image_url} alt="Package" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setFormData({ ...formData, image_url: "" })} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" data-testid="pkg-image-remove">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-20 rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                        <ImageIcon className="w-6 h-6 text-gray-300" />
                      </div>
                    )}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors" data-testid="pkg-image-upload-label">
                      <Upload className="w-4 h-4" />
                      {uploading ? "Uploading..." : (formData.image_url ? "Replace Image" : "Upload Image")}
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e.target.files?.[0])} disabled={uploading} data-testid="pkg-image-upload-input" />
                    </label>
                  </div>
                </div>
              </TabsContent>

              {/* Itinerary Tab */}
              <TabsContent value="itinerary" className="space-y-3 mt-4">
                {/* Manual / Auto Toggle */}
                <div className="flex items-center justify-end gap-3 p-2 bg-gray-50 rounded-md border border-gray-200">
                  <span className={`text-xs font-medium ${formData.itinerary_mode === 'manual' ? 'text-gray-800' : 'text-gray-400'}`}>Manual</span>
                  <Switch
                    checked={formData.itinerary_mode === 'auto'}
                    onCheckedChange={v => setFormData({ ...formData, itinerary_mode: v ? 'auto' : 'manual' })}
                    data-testid="itinerary-mode-toggle"
                  />
                  <span className={`text-xs font-medium ${formData.itinerary_mode === 'auto' ? 'text-emerald-700' : 'text-gray-400'}`}>Auto</span>
                  <span className="text-[10px] text-gray-400 ml-2">
                    {formData.itinerary_mode === 'auto' ? 'Pick activities from catalog' : 'Type activities as comma-separated list'}
                  </span>
                </div>

                {formData.itinerary.map((day, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 relative" data-testid={`itin-day-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">{day.day}</span>
                        <span className="text-sm font-semibold text-gray-700">Day {day.day}</span>
                      </div>
                      {formData.itinerary.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItineraryDay(idx)} className="text-red-400 hover:text-red-600 h-7 w-7 p-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Input value={day.title} onChange={e => updateItineraryDay(idx, 'title', e.target.value)} placeholder="e.g. Arrival & Munnar Transfer" className="bg-white border-gray-200 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Textarea value={day.description} onChange={e => updateItineraryDay(idx, 'description', e.target.value)} rows={2} placeholder="Day activities and description..." className="bg-white border-gray-200 text-sm" />
                      </div>

                      {formData.itinerary_mode === 'manual' ? (
                        <div>
                          <Label className="text-xs">Activities (comma-separated)</Label>
                          <Input value={day.activities} onChange={e => updateItineraryDay(idx, 'activities', e.target.value)} placeholder="e.g. Mattupetty Dam, Tea Museum, Echo Point" className="bg-white border-gray-200 text-sm" />
                        </div>
                      ) : (
                        <div>
                          <Label className="text-xs">Activities</Label>
                          <div className="mt-1 space-y-2">
                            {(day.activity_items || []).length === 0 && (
                              <p className="text-xs text-gray-400 italic">No activities yet. Click "+ Activity" to pick from catalog.</p>
                            )}
                            {(day.activity_items || []).map((act, aIdx) => (
                              <div key={`${act.id}-${aIdx}`} className="flex items-start justify-between gap-2 p-2 bg-white border border-emerald-200 rounded" data-testid={`day-${idx}-activity-${aIdx}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">{act.title}</p>
                                  {act.destination && <p className="text-[10px] text-gray-500">{act.destination}</p>}
                                  {act.description && <p className="text-[11px] text-gray-500 line-clamp-1">{act.description}</p>}
                                  {act.price > 0 && <p className="text-[11px] text-emerald-600 font-semibold">₹{Number(act.price).toLocaleString()}</p>}
                                </div>
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeActivityFromDay(idx, aIdx)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ))}
                            <Button type="button" size="sm" variant="outline" onClick={() => openActivityPicker(idx)} className="border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50 w-full" data-testid={`add-activity-day-${idx}`}>
                              <Plus className="w-3.5 h-3.5 mr-1" /> Activity
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addItineraryDay} className="w-full border-dashed border-gray-300 text-gray-500" data-testid="add-itin-day">
                  <Plus className="w-4 h-4 mr-2" /> Add Day {formData.itinerary.length + 1}
                </Button>
              </TabsContent>

              {/* Pricing Tab */}
              <TabsContent value="pricing" className="space-y-5 mt-4">
                {/* Hotel Options */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-gray-800 text-base font-semibold flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-emerald-600" /> Hotel Options
                      </Label>
                      <p className="text-[11px] text-gray-500 mt-0.5">Add multiple hotel options. Each option can include multiple stays (e.g. 2N Munnar + 1N Thekkady) and its own pricing grid.</p>
                    </div>
                    <Button type="button" onClick={addHotelOption} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="add-hotel-option-btn">
                      <Plus className="w-4 h-4" /> Add Hotel Option
                    </Button>
                  </div>

                  {formData.hotel_options.length === 0 && (
                    <div className="py-8 text-center border border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No hotel options yet. Click "Add Hotel Option" to start.</p>
                    </div>
                  )}

                  {formData.hotel_options.map((opt, optIdx) => (
                    <HotelOptionCard
                      key={opt.id}
                      optIdx={optIdx}
                      opt={opt}
                      onRemove={removeHotelOption}
                      onAddStay={addStay}
                      onRemoveStay={removeStay}
                      onUpdateStay={updateStay}
                      onAddMealPlan={addMealPlan}
                      onRemoveMealPlan={removeMealPlan}
                      onAddPaxRow={addPaxRow}
                      onRemovePaxRow={removePaxRow}
                      onUpdatePaxRow={updatePaxRow}
                      onUpdateOpt={updateHotelOption}
                    />
                  ))}
                </div>

                {/* Legacy fallback pricing (kept for simple packages with no hotel options) */}
                {formData.hotel_options.length === 0 && (
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                    <div>
                      <Label>Per CWB Price (INR) <span className="text-[10px] text-gray-400">(fallback)</span></Label>
                      <Input type="number" value={formData.per_cwb_price} onChange={e => setFormData({ ...formData, per_cwb_price: e.target.value })} placeholder="0" className="bg-gray-50 border-gray-200" data-testid="pkg-cwb-price" />
                    </div>
                    <div>
                      <Label>Per CNB Price (INR) <span className="text-[10px] text-gray-400">(fallback)</span></Label>
                      <Input type="number" value={formData.per_cnb_price} onChange={e => setFormData({ ...formData, per_cnb_price: e.target.value })} placeholder="0" className="bg-gray-50 border-gray-200" data-testid="pkg-cnb-price" />
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4 mt-4">
                <div>
                  <Label>Highlights (one per line)</Label>
                  <Textarea value={formData.highlights} onChange={e => setFormData({ ...formData, highlights: e.target.value })} rows={4} placeholder="Hill station experience&#10;Tea plantation visit&#10;Houseboat stay" className="bg-gray-50 border-gray-200" />
                </div>
                <div>
                  <Label>Inclusions (one per line)</Label>
                  <Textarea value={formData.inclusions} onChange={e => setFormData({ ...formData, inclusions: e.target.value })} rows={4} placeholder="Accommodation&#10;Breakfast&#10;Airport transfers" className="bg-gray-50 border-gray-200" data-testid="pkg-inclusions" />
                </div>
                <div>
                  <Label>Exclusions (one per line)</Label>
                  <Textarea value={formData.exclusions} onChange={e => setFormData({ ...formData, exclusions: e.target.value })} rows={4} placeholder="Flights&#10;Personal expenses&#10;Entry tickets" className="bg-gray-50 border-gray-200" data-testid="pkg-exclusions" />
                </div>
                <div>
                  <Label>Terms &amp; Conditions</Label>
                  <Textarea value={formData.terms_conditions} onChange={e => setFormData({ ...formData, terms_conditions: e.target.value })} rows={6} placeholder="Booking terms, cancellation policy, payment schedule, etc." className="bg-gray-50 border-gray-200" data-testid="pkg-terms" />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="save-package-btn">
                {editingPackage ? 'Update' : 'Create'} Package
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }} className="border-gray-300">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Activity Picker Modal (Auto-mode) */}
      <Dialog open={activityModal.open} onOpenChange={(v) => setActivityModal(prev => ({ ...prev, open: v }))}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-3xl h-[80vh] flex flex-col" data-testid="activity-picker-modal">
          <DialogHeader>
            <DialogTitle>Add Activity {activityModal.dayIdx !== null ? `for Day ${formData.itinerary[activityModal.dayIdx]?.day}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="p-1">
            <Input
              placeholder={`Search ${formData.destination || ''} activities...`}
              className="bg-gray-50 border-gray-200"
              value={activitySearch}
              onChange={e => setActivitySearch(e.target.value)}
              data-testid="activity-search-input"
            />
            {formData.destination && (
              <p className="text-[11px] text-gray-500 mt-2">
                Showing <strong className="text-emerald-700">{formData.trip_type}</strong> activities for <strong className="text-emerald-700">{formData.destination}</strong> only
              </p>
            )}
            {!formData.destination && (
              <p className="text-[11px] text-amber-600 mt-2">Select a Destination in Basic Info to see relevant activities.</p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(() => {
                const q = activitySearch.trim().toLowerCase();
                const pkgDest = (formData.destination || "").trim().toLowerCase();
                const pkgTripType = (formData.trip_type || "domestic").toLowerCase();
                const filtered = sightseeings.filter(t => {
                  // 1) Strict region_type match (domestic / international)
                  const tRegion = (t.region_type || "domestic").toLowerCase();
                  if (tRegion !== pkgTripType) return false;
                  // 2) Strict destination match (must equal package destination)
                  const tDest = (t.destination || "").trim().toLowerCase();
                  if (pkgDest && tDest !== pkgDest) return false;
                  // 3) Optional search on top
                  if (q) {
                    const tName = (t.tour_name || t.name || "").toLowerCase();
                    if (!tName.includes(q) && !tDest.includes(q)) return false;
                  }
                  return true;
                });
                if (filtered.length === 0) {
                  return (
                    <p className="text-gray-500 col-span-2 text-center py-8 text-sm">
                      {pkgDest
                        ? <>No <strong className="text-emerald-700">{pkgTripType}</strong> activities found for <strong className="text-emerald-700">{formData.destination}</strong>. Add them from Admin &gt; Resources &gt; Sightseeing.</>
                        : <>Please select a Trip Type and Destination first in the Basic Info tab.</>}
                    </p>
                  );
                }
                return filtered.map(tour => (
                  <div key={tour.id} className="p-3 border border-gray-200 rounded bg-gray-50/50 flex justify-between items-start hover:border-emerald-400 transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="text-gray-800 font-medium text-sm truncate">{tour.tour_name || tour.name}</p>
                      <p className="text-xs text-gray-500">{tour.destination}</p>
                      {tour.description && <p className="text-[11px] text-gray-400 mt-1 line-clamp-2">{tour.description}</p>}
                      {tour.price_adult > 0 && <p className="text-emerald-600 font-semibold text-sm mt-1">₹{Number(tour.price_adult).toLocaleString()}</p>}
                    </div>
                    <Button type="button" size="sm" className="bg-emerald-600 hover:bg-emerald-700 shrink-0 ml-3" onClick={() => addActivityToDay(tour)} data-testid={`pick-activity-${tour.id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                ));
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// ===================== Hotel Option Card =====================
const HotelOptionCard = ({ optIdx, opt, onRemove, onAddStay, onRemoveStay, onUpdateStay, onAddMealPlan, onRemoveMealPlan, onAddPaxRow, onRemovePaxRow, onUpdatePaxRow, onUpdateOpt }) => {
  const [newMealPlan, setNewMealPlan] = useState("");

  const handleAddPlan = () => {
    if (newMealPlan.trim()) {
      onAddMealPlan(optIdx, newMealPlan);
      setNewMealPlan("");
    }
  };

  return (
    <div className="p-4 bg-emerald-50/30 border-2 border-emerald-200 rounded-lg space-y-4" data-testid={`hotel-option-${optIdx}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">{optIdx + 1}</span>
          <span className="text-sm font-bold text-gray-800">Hotel Option {optIdx + 1}</span>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(optIdx)} className="text-red-500 hover:text-red-700" data-testid={`remove-hotel-option-${optIdx}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Stays list — multiple hotel segments (e.g. 2N Munnar + 1N Thekkady) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-gray-600 font-semibold uppercase tracking-wide">Stays / Segments</Label>
          <Button type="button" size="sm" onClick={() => onAddStay(optIdx)} className="h-7 bg-emerald-600 hover:bg-emerald-700 gap-1 text-xs" data-testid={`add-stay-${optIdx}`}>
            <Plus className="w-3 h-3" /> Add Stay
          </Button>
        </div>
        {(opt.stays || []).map((stay, stayIdx) => (
          <div key={stayIdx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-gray-200" data-testid={`opt-${optIdx}-stay-${stayIdx}`}>
            <div className="col-span-1 flex items-center justify-center">
              <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-[11px] font-bold flex items-center justify-center">{stayIdx + 1}</span>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] text-gray-500">Nights</Label>
              <Input value={stay.nights} onChange={e => onUpdateStay(optIdx, stayIdx, 'nights', e.target.value)} placeholder="e.g. 2N" className="h-8 bg-white border-gray-200 text-sm" data-testid={`opt-${optIdx}-stay-${stayIdx}-nights`} />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] text-gray-500">Destination</Label>
              <Input value={stay.destination} onChange={e => onUpdateStay(optIdx, stayIdx, 'destination', e.target.value)} placeholder="e.g. Munnar" className="h-8 bg-white border-gray-200 text-sm" data-testid={`opt-${optIdx}-stay-${stayIdx}-dest`} />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] text-gray-500">Hotel Name</Label>
              <Input value={stay.hotel_name} onChange={e => onUpdateStay(optIdx, stayIdx, 'hotel_name', e.target.value)} placeholder="e.g. Hotel Amuse Vista" className="h-8 bg-white border-gray-200 text-sm" data-testid={`opt-${optIdx}-stay-${stayIdx}-hotel`} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] text-gray-500">Room Category</Label>
              <Input value={stay.room_category} onChange={e => onUpdateStay(optIdx, stayIdx, 'room_category', e.target.value)} placeholder="e.g. Deluxe" className="h-8 bg-white border-gray-200 text-sm" data-testid={`opt-${optIdx}-stay-${stayIdx}-room`} />
            </div>
            <div className="col-span-1 flex items-center justify-center">
              {(opt.stays || []).length > 1 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveStay(optIdx, stayIdx)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600" data-testid={`remove-stay-${optIdx}-${stayIdx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Meal plans */}
      <div>
        <Label className="text-xs text-gray-600 mb-1 block">Meal Plans</Label>
        <div className="flex flex-wrap items-center gap-2">
          {opt.meal_plans.map(plan => (
            <Badge key={plan} className={`${plan === 'CPAI' ? 'bg-emerald-600' : 'bg-gray-700'} text-white gap-1 py-1 px-2`}>
              {plan}
              {plan !== "CPAI" && (
                <button type="button" onClick={() => onRemoveMealPlan(optIdx, plan)} className="ml-1 hover:text-red-200" data-testid={`remove-plan-${optIdx}-${plan}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
          <div className="flex items-center gap-1">
            <Input
              value={newMealPlan}
              onChange={e => setNewMealPlan(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddPlan(); } }}
              placeholder="Add plan (e.g. MAPAI, AP)"
              className="h-8 bg-white border-gray-200 text-xs w-40"
              data-testid={`new-plan-input-${optIdx}`}
            />
            <Button type="button" size="sm" onClick={handleAddPlan} className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid={`add-plan-btn-${optIdx}`}>
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Pricing Grid */}
      <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-2 px-3 text-left font-semibold text-gray-700 text-xs">PAX</th>
                {opt.meal_plans.map(plan => (
                  <th key={plan} className="py-2 px-3 text-left font-semibold text-gray-700 text-xs">{plan} (INR PP)</th>
                ))}
                <th className="py-2 px-3 text-left font-semibold text-gray-700 text-xs">VEHICLE</th>
                <th className="py-2 px-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {opt.pax_rows.map((row, rowIdx) => (
                <tr key={`${row.pax}-${rowIdx}`} className={`border-b border-gray-100 ${row.pax === 2 ? 'bg-amber-50/40' : ''}`}>
                  <td className="py-1.5 px-3 w-28">
                    <Input
                      type="number"
                      value={row.pax}
                      disabled={row.pax === 2}
                      onChange={e => onUpdatePaxRow(optIdx, rowIdx, 'pax', e.target.value)}
                      className="h-8 bg-white border-gray-200 text-sm"
                      data-testid={`opt-${optIdx}-row-${rowIdx}-pax`}
                    />
                  </td>
                  {opt.meal_plans.map(plan => (
                    <td key={plan} className="py-1.5 px-3">
                      <Input
                        type="number"
                        value={row.prices[plan] ?? ""}
                        onChange={e => onUpdatePaxRow(optIdx, rowIdx, plan, e.target.value)}
                        placeholder="0"
                        className="h-8 bg-white border-gray-200 text-sm"
                        data-testid={`opt-${optIdx}-row-${rowIdx}-${plan}`}
                      />
                    </td>
                  ))}
                  <td className="py-1.5 px-3">
                    <Input
                      value={row.vehicle}
                      onChange={e => onUpdatePaxRow(optIdx, rowIdx, 'vehicle', e.target.value)}
                      placeholder="e.g. 12s TT"
                      className="h-8 bg-white border-gray-200 text-sm"
                      data-testid={`opt-${optIdx}-row-${rowIdx}-vehicle`}
                    />
                  </td>
                  <td className="py-1.5 px-2">
                    {row.pax !== 2 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => onRemovePaxRow(optIdx, rowIdx)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600" data-testid={`remove-row-${optIdx}-${rowIdx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-2 border-t border-gray-100 bg-gray-50">
          <Button type="button" variant="outline" size="sm" onClick={() => onAddPaxRow(optIdx)} className="w-full border-dashed border-gray-300 text-gray-600 text-xs" data-testid={`add-pax-row-${optIdx}`}>
            <Plus className="w-3 h-3 mr-1" /> Add PAX Row
          </Button>
        </div>
      </div>

      {/* CWB / CNB per hotel option */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Per CWB Price (INR)</Label>
          <Input type="number" value={opt.cwb_price} onChange={e => onUpdateOpt(optIdx, 'cwb_price', e.target.value)} placeholder="0" className="bg-white border-gray-200 text-sm" data-testid={`opt-${optIdx}-cwb`} />
        </div>
        <div>
          <Label className="text-xs">Per CNB Price (INR)</Label>
          <Input type="number" value={opt.cnb_price} onChange={e => onUpdateOpt(optIdx, 'cnb_price', e.target.value)} placeholder="0" className="bg-white border-gray-200 text-sm" data-testid={`opt-${optIdx}-cnb`} />
        </div>
      </div>
    </div>
  );
};

export default AdminPackages;
